import { useRef, useEffect } from 'react';
import type { CallbackFn } from './types';

/** A wrapper around setTimeout that plays better with React hooks.
 * @example useTimeout(() => console.log('hej'), 5) // this will be executed once 5 ms
 * @example useTimeout(() => console.log('hej'), 10) // changing the interval will cancel the previous timeout
 */
export const useTimeout = (callback: CallbackFn, delay: number | null) => {
  const savedCallback = useRef<CallbackFn | null>(null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect((): void | (() => void) => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }
    if (delay !== null) {
      const id = setTimeout(tick, delay);
      return () => {
        return clearTimeout(id);
      };
    }
  }, [delay]);
};
